import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {of} from 'rxjs/observable/of';
import {cloneDeep, get} from 'lodash';
import {BehaviorSubject} from 'rxjs';

import {ApplicationConfigService} from '@ispm/navigation/services/applicationConfig.service';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {
  INavigationApplicationInterface,
  INavigationBarItemInterface,
  INavigationConfigurationInterface,
  IRegionInterface
} from '../interfaces';
import {NAVIGATION_ACTIONS} from '../store/navigation.actions';
import {RouterManagerService} from '@ispm/core/services/router-manager.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {
  selectCurrentSelectedApplication,
  selectNavigationConfiguration,
  selectNavigationItemByAppId
} from '../store/navigation.selector';
import {selectApplicationsConfiguration} from '@ispm/user/store/user.selector';
import {WorkflowService} from '@ispm/workflow/services/workflow.service';
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {ActivatedRoute, Router} from '@angular/router';
import {InterpolateService} from "@ispm/shared/services/interpolate.service";


const requestWebConfigUri = '/workflow/dashboardConfig';

@Injectable()
export class NavigationConfigService {

  private DEFAULT: string = 'default';
  private _clickedNavItem$ = new BehaviorSubject<INavigationBarItemInterface>(null);
  private disableAllMenuItems = false;
  private fetchWebConfig = false;
  private loadedConfig = false;
  private organisationConfig = {};

  constructor(
    private _http: HttpInterceptorService,
    private interpolateService: InterpolateService,
    private authService: AuthenticationService,
    private store: Store<IgenomicsState>,
    private userActionCreatorService: UserActionCreatorService,
    private appService: ApplicationConfigService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private workflowService: WorkflowService,
    private routerManagerService: RouterManagerService
  ) {
  }

  public getUrlQueryParams(url) {
    const parts = url.split('?');
    let queryParams = {};
    if (parts.length > 1) {
      const queryParamsKeyVal = parts[1].split('&');
      for (let i = 0; i < queryParamsKeyVal.length; i++) {
        const keyVal = queryParamsKeyVal[i].split('=');
        queryParams[keyVal[0]] = keyVal[1];
      }
    }
    return queryParams
  }

  public setClickedNavItem(navItem: INavigationBarItemInterface) {
    this._clickedNavItem$.next(navItem);
  }

  get clickedNavItem$(): BehaviorSubject<INavigationBarItemInterface> {
    return this._clickedNavItem$;
  }

  public requestWebConfiguration(force = false) {
    if (this.fetchWebConfig && !force) {
      return this.getWebConfiguration();
    }

    this.fetchWebConfig = true;
    const isAdmin = this.authService.isAdminUser(); // this.isAdminUser(this.authService.user);// this.authService.user.role === 'GenomicsAdminGroup';

    return this._http
      .get(requestWebConfigUri)
      .pipe(
        map(data => data.body),
        switchMap((navigationConfig: INavigationConfigurationInterface) => {
          this.workflowService.webConfigReq = Promise.resolve(cloneDeep(navigationConfig));
          this.disableAllMenuItems = false;
          if (!this.authService.isAdminUser()) {
            return this.handleOrgSettingParam(navigationConfig);
          } else {
            if (this.authService.user.organization != this.authService.user.originalOrganization) { // WHEN ADMIN DO LOGIN AS
              return this.handleOrgSettingParam(navigationConfig);
            } else { // we on the admin pages so disable all menu items
              this.disableAllMenuItems = (this.authService.user.role === 'GenomicsAdminGroup');
            }
            return of(navigationConfig);
          }
        }),
        switchMap((navigationConfig: INavigationConfigurationInterface) => {
          return this.appService.getAppConfiguration(navigationConfig, isAdmin);
        }),
        map((navigationConfig: INavigationConfigurationInterface) => {
          navigationConfig.navigation.items.forEach(navItem => {
            navItem.allowInitialLoad = true;
            navItem.appid = navItem.appid || this.DEFAULT;

            if (navItem.hasOwnProperty('type') && navItem.type.toLowerCase() === 'patient') {
              navItem.redirectUrl = '/Pathologist/patients?resource=' + navItem.type.toLowerCase(); // hot fix for the redirect for patient else it be on same place and test
            }

            if (!navigationConfig.isAppConfigEnabled) {
              navItem = this.verifyOrganizationPermission(navItem);
            }

            if (!isAdmin) {
              navItem = this.verifyUserPermission(navItem);
            } else {
              // it admin user so no need check permission just verify how to display the navigation
              navItem = this.verifyAdminUserPermission(navItem);
            }
          });

          this.configureApplications(navigationConfig);
          this.setWebConfiguration(navigationConfig);
          this.loadedConfig = true;

          return navigationConfig;
        }),
        take(1)
      );
  }

  public checkingUserPermission($state, navigationConfig) {
    let hasPermission = true;

    if (this.authService.isAdminUser()) {
      return true;
    }

    const currentState = this.router.url;
    let navigationItems = [];

    if (navigationConfig.navigation.hasOwnProperty('items') && Array.isArray(navigationConfig.navigation['items'])) {
      navigationItems = navigationConfig.navigation.items;
    } else if (!navigationConfig.navigation.hasOwnProperty('items') && Array.isArray(navigationConfig.navigation)) {
      navigationItems = navigationConfig.navigation;
    }

    const locateItem = navigationItems.find(navItem => this.getStateFromUri(navItem) === currentState && navItem.appid === navigationConfig.currentApp.id);

    if (locateItem) {
      if (locateItem.hasOwnProperty('requiredPermissions')) {
        hasPermission = locateItem.requiredPermissions.every(permission => this.authService.doesUserHasPermission(permission));
      }
    }

    return hasPermission;
  }

  public getStateFromUri(navItem: INavigationBarItemInterface) {
    return this.getStateNameFromUri(navItem.redirectUrl);
  }

  public getStateNameFromUri(redirectUrl: string): string {
    return redirectUrl.split(/[?#]/)[0];
  }

  public setWebConfiguration(webConfig: any) {
    this.store.dispatch({
      type: NAVIGATION_ACTIONS.SET_WEB_CONFIG,
      payload: webConfig
    });
  }

  public getWebConfiguration(): Observable<INavigationConfigurationInterface> {
    return this.store.pipe(select('navigation')).pipe(filter(data => !!data), take(1));
  }

  public getCurrentAppLocalization(currentApp: INavigationApplicationInterface): Observable<any> {
    let local;

    if (this.authService.user && this.authService.user.role && !this.authService.isAdminUser() && currentApp) {
      return this.getWebConfiguration().pipe(map((webConfig) => {
          let localization: Array<IRegionInterface> = null;
          let appIndex;
          const apps = webConfig.navigation.applications;
          for (appIndex = 0; appIndex < apps.length; appIndex++) {
            if (apps[appIndex].id == currentApp.id) {
              localization = apps[appIndex].localization;
              break;
            }
          }
          return localization;
        }),
        switchMap(localization => {
          return this.userActionCreatorService.getStoredOrganizationConfiguration().pipe(map(orgDetails => ([localization, orgDetails])));
        }),
        map((rsltCol) => {
          let country;
          const orgDetails = rsltCol[1];
          const localization = rsltCol[0];

          if (!Array.isArray(localization)) {
            return null;
          }

          if (orgDetails && orgDetails.address && orgDetails.address[0].country) {
            country = get(orgDetails, 'address[0].country', 'default');
          }

          local = localization.find((locale) => locale.region.find((itm) => itm.display == country) != null);

          if (local == null) {
            local = localization.find((locale) => locale.region.find((itm) => itm.display == 'default') != null);
          }

          return local;
        })
      );
    } else {
      return of(null);
    }
  }

  public getWebConfigurationByAppId(appid: string): Observable<Array<INavigationBarItemInterface>> {
    return this.store.pipe(select(selectNavigationItemByAppId, {id: appid})).pipe(filter(data => !!data));
  }

  public setCurrentApp(app: INavigationApplicationInterface) {
    sessionStorage.setItem('currentApplication', JSON.stringify(app));
    sessionStorage.setItem('appName', app.text);
    this.store.dispatch({
      type: NAVIGATION_ACTIONS.SET_CURRENT_APP,
      payload: app
    });
  }

  public getCurrentApp(): Observable<INavigationApplicationInterface> {
    return this.store.pipe(select(selectCurrentSelectedApplication)).pipe(
      filter(app => !!app));
  }

  public setCurrentAppSelectedTab(tab: string) {
    this.store.dispatch({
      type: NAVIGATION_ACTIONS.SET_CURRENT_APP_SELECTED_TAB,
      payload: tab
    });
  }

  public getCurrentAppFromSessionStorage(): INavigationApplicationInterface {
    return JSON.parse(sessionStorage.getItem('currentApplication'));
  }

  public getCurrentApplicationMemoryType(): boolean {
    return !!this.getCurrentAppFromSessionStorage()?.testMemory;
  }

  public getCurrentApplicationId(): string {
    const application = this.getCurrentAppFromSessionStorage();

    if (application && application.id) {
      return application.id;
    }

    return '';
  }

  public getCurrentApplication(): Observable<any> {
    return this.store.pipe(select(selectCurrentSelectedApplication));
  }

  public getNavigationConfig(): Observable<INavigationConfigurationInterface> {
    return this.store.pipe(select(selectNavigationConfiguration)).pipe(
      filter(config => !!config && config.navigation != null));
  }

  public getCurrentAppBrandData(): Observable<string> {
    return this.store
      .pipe(
        select(selectApplicationsConfiguration),
        withLatestFrom(this.getCurrentApplication()),
        map(([applications, currentApp]: [any[], INavigationApplicationInterface]) => applications.find(application => application.appId === currentApp.id)),
        map((application: any) => application.brand)
      );
  }

  public getCurrentApplicationType(): string {
    const application = this.getCurrentAppFromSessionStorage();

    return !!application ? get(application, 'applicationType', '') : '';
  }

  private configureApplications(navigationConfig: INavigationConfigurationInterface): void {
    const role = this.authService.user.role;
    const selected = navigationConfig.navigation.selected;
    let itemId = selected.hasOwnProperty(role) ? selected[role] : undefined;

    if (itemId === undefined && this.authService.isAdminUser()) {
      itemId = selected.hasOwnProperty('Pathologist') ? selected['Pathologist'] : undefined;
    }

    const urlParams = this.routerManagerService.getUrlParams(window.location.pathname + window.location.search);
    let navItem: INavigationBarItemInterface = null;
    const appNameStr = 'appname';
    let paramValue = urlParams.queryParams.appname;

    if (urlParams.queryParams.testNumber && urlParams.queryParams.appname) {
      paramValue = urlParams.queryParams.appname;
    }

    if (paramValue && paramValue !== '') {
      paramValue = paramValue.toLowerCase();
      navItem = navigationConfig.navigation.items.find(item => (item.redirectUrl !== '' && item.redirectUrl.toLowerCase().includes('?' + appNameStr + '=' + paramValue)));

      if (!navItem) {
        navItem = navigationConfig.navigation.items.find(item => item.identify === itemId && itemId);
      }
    } else {
      navItem = navigationConfig.navigation.items.find(item => item.identify === itemId && itemId);
    }

    if (navigationConfig.navigation.applications) {
      navigationConfig.navigation.applications.map(app => {
        app.allowInitialLoad = true;
        if (app.type.toLowerCase() !== 'header') {
          const tabPresent = navigationConfig.navigation.items.find(item =>
            item.appid === app.id && item.allowInitialLoad && item.enable);
          if (tabPresent) {
            if (navItem && app.id === navItem.appid) {
              if ((app.alias && app.alias !== '' && paramValue) && app.alias.toLowerCase() === paramValue) {
                navigationConfig.currentApp = app; // navigationConfig.navigation.applications.find(item=>(item.alias && item.alias!="") && (item.alias.toLowerCase()===paramValue));

                this.setCurrentApp(navigationConfig.currentApp);
              } else {
                navigationConfig.currentApp = app;
                this.setCurrentApp(app);
              }
            }
          } else {
            app.allowInitialLoad = false;
            app.enable = false;
          }
        }
      });
      if (!navigationConfig.navigation.applications.find(app => app.type === 'application')) {
        navigationConfig.navigation.applications = this.appService.getDefaultApplication();
        navigationConfig.currentApp = navigationConfig.navigation.applications[0];
        this.setCurrentApp(navigationConfig.currentApp);
      }
      if (navigationConfig.navigation.applications.find(app => app.type === 'application') && !navigationConfig.currentApp) {
        const _apps = navigationConfig.navigation.applications.filter(
          (obj: any) => (obj.enable));

        if (Array.isArray(_apps) && _apps.length > 0) {
          navigationConfig.currentApp = _apps[0];
          this.setCurrentApp(navigationConfig.currentApp);
        }
      }
    } else {
      navigationConfig.navigation.applications = this.appService.getDefaultApplication();
      navigationConfig.currentApp = navigationConfig.navigation.applications[0];
      this.setCurrentApp(navigationConfig.currentApp);
    }
  }

  private verifyOrganizationPermission(navItem: INavigationBarItemInterface) {
    if (navItem.hasOwnProperty('organizationSourceFields')) {
      const properties = Object.getOwnPropertyNames(navItem.organizationSourceFields);
      properties.forEach(property => {
        const propertyValue = navItem.organizationSourceFields[property];
        switch (property) {
          case 'disablePath':
            navItem.enable = !this.organisationConfig[propertyValue];
            break;
          case 'enablePath':
            navItem.enable = this.organisationConfig[propertyValue];
            break;
          case 'labelPath':
            navItem.text = this.organisationConfig[propertyValue] || '';
        }
      });
    } else {
      // if we don't have this property then we work as fall back
      if (navItem.hasOwnProperty('type') && navItem.type.toLowerCase() === 'test') {
        navItem.enable = !this.organisationConfig['disableWorklist'];
      }
      if (navItem.hasOwnProperty('type') && navItem.type.toLowerCase() === 'curation') {
        navItem.enable = this.organisationConfig['enableCuration'];
        navItem.text = this.organisationConfig['curationWorklistLabel'] || '';
      }
    }
    return navItem;
  }

  private verifyUserPermission(navItem: INavigationBarItemInterface) {
    if (navItem.hasOwnProperty('requiredPermissions')) {
      navItem.requiredPermissions.forEach(permission => {
        if (!this.authService.doesUserHasPermission(permission) || this.verifyNavigationPermissions(permission)) {
          navItem.enable = false;
        }
      });
    }
    return navItem;
  }

  private verifyNavigationPermissions(permission: string): boolean {
    // TODO: NAVIGATION_ITEM.PATHWAYS.INVISIBLETAB permission and
    //  ['NAVIGATION_ITEM.CURATION_LIST', 'NAVIGATION_ITEM.PATIENT_LIST', 'NAVIGATION_ITEM.WORKLIST'] permissions are
    //  mutually exclusive but can be selected by admin together - the bug!!!. This temp. workaround fixes the issue (GEN-208675)
    //  and should be removed/modified when the admin will be fixed.
    if (permission === 'NAVIGATION_ITEM.PATHWAYS.INVISIBLETAB') {
      // verify is NAVIGATION_ITEM.PATHWAYS.INVISIBLETAB permission is selected together with at least one of the others
      return ['NAVIGATION_ITEM.CURATION_LIST', 'NAVIGATION_ITEM.PATIENT_LIST', 'NAVIGATION_ITEM.WORKLIST'].some(pm => this.authService.user.permissions.includes(pm));
   }

    return false;
  }

  private verifyAdminUserPermission(navItem: INavigationBarItemInterface) {
    // we need verify if admin user login dont login as  to some org so we wont disable the navigation item
    if (this.authService.user.organization == this.authService.user.originalOrganization) {
      // this the admin pages so just make sure if what ever of any case to removed  the navigation items so nothing will display
      if (this.disableAllMenuItems) {
        navItem.enable = false;
      }
    }
    return navItem;
  }

  private handleOrgSettingParam(navigationConfig): Observable<INavigationConfigurationInterface> {
    return this.userActionCreatorService.getStore().select('organizationConfiguration').pipe(
      filter(data => !!data),
      tap(data => {
        this.organisationConfig = data;
      }),
      map(() => navigationConfig));
  }
}
